import type {AutocompleteRenderGetTagProps} from '@mui/material/Autocomplete';
import type {FocusEventHandler} from 'react';
import Box from '@mui/material/Box';
import IconButton, {type IconButtonProps} from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {alpha} from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import {useTranslation} from 'react-i18next';

import type {ResolvedOption} from './selectOption';

const CHIP_HEIGHT = 30;

interface ValueChipProps {
    option: ResolvedOption;
    tagProps: ReturnType<AutocompleteRenderGetTagProps>;
    onEdit?: (value: string) => void;
    onFocus?: FocusEventHandler<HTMLSpanElement>;
}

const ValueChip = ({option, tagProps, onEdit, onFocus}: ValueChipProps) => {
    const {t} = useTranslation();
    const {key: _key, onDelete, disabled, ...chipProps} = tagProps;
    const actionProps: IconButtonProps = {
        disabled,
        onMouseDown: event => {
            // Keep input focus and prevent the chip's opener from handling this press.
            event.preventDefault();
            event.stopPropagation();
        },
        onKeyDown: event => {
            // Isolate Autocomplete navigation while preserving native button activation.
            event.stopPropagation();
        },
        sx: theme => ({
            flexShrink: 0,
            width: 26,
            height: 26,
            p: 0,
            borderRadius: '50%',
            color: 'text.secondary',
            '&:hover': {
                color: 'text.primary',
                bgcolor: option.tone ? alpha(theme.palette[option.tone].main, 0.16) : 'action.hover'
            },
            '&.Mui-focusVisible': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 1
            }
        })
    };

    return (
        <Box
            component={'span'}
            {...chipProps}
            onFocus={onFocus}
            sx={theme => {
                const tone = option.tone ? theme.palette[option.tone].main : undefined;

                return {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    minWidth: 0,
                    pl: 0.75,
                    pr: 0.25,
                    border: '1px solid',
                    borderColor: tone ? alpha(tone, 0.4) : '#DDE1E6',
                    borderRadius: '6px',
                    bgcolor: tone ? alpha(tone, 0.08) : '#F3F4F6',
                    color: option.unavailable ? 'warning.dark' : 'text.primary',
                    // Override the theme's 26px mobile tag height.
                    '&.MuiAutocomplete-tag': {height: CHIP_HEIGHT}
                };
            }}
        >
            {option.adornment}

            <Typography component={'span'} variant={'body2'} noWrap sx={{minWidth: 0, maxWidth: 300, fontSize: 13, fontWeight: 400}}>
                {option.label}
            </Typography>

            {!!onEdit && (
                <IconButton
                    {...actionProps}
                    aria-label={`${t('Edit')} ${option.label}`}
                    onClick={event => {
                        event.stopPropagation();
                        onEdit(option.value);
                    }}
                >
                    <EditIcon sx={{fontSize: 16}}/>
                </IconButton>
            )}

            <IconButton
                {...actionProps}
                aria-label={`${t('Remove')} ${option.label}`}
                onClick={event => {
                    event.stopPropagation();
                    onDelete(event);
                }}
            >
                <CloseIcon sx={{fontSize: 16}}/>
            </IconButton>
        </Box>
    );
};

export default ValueChip;
